/**
 * StaleDataIndicator Component
 *
 * Displays the data freshness status, showing when data was last fetched.
 * Provides visual feedback when data might be outdated (stale) and when
 * the user is offline viewing cached data.
 *
 * Features:
 * - Shows relative time since last update (e.g., "5 minutes ago")
 * - Visual warning when data is stale
 * - Integrates with offline status to show cached data warning
 * - Optional refresh callback for manual data refresh
 *
 * Usage:
 * ```tsx
 * import { StaleDataIndicator } from "@/components/ui/stale-data-indicator";
 *
 * function MyDataView() {
 *   const { dataUpdatedAt, refetch, isFetching } = useMyQuery();
 *
 *   return (
 *     <div>
 *       <StaleDataIndicator
 *         dataUpdatedAt={dataUpdatedAt}
 *         onRefresh={refetch}
 *         isRefreshing={isFetching}
 *       />
 *       <MyContent />
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect } from "react";
import { Clock, RefreshCw, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatLastUpdated, isDataStale } from "@/lib/formatRelativeTime";

// ============================================================================
// Types
// ============================================================================

export interface StaleDataIndicatorProps {
  /**
   * Timestamp of when the data was last successfully fetched.
   * This is typically from React Query's dataUpdatedAt.
   */
  dataUpdatedAt: number | Date | null | undefined;

  /**
   * Whether the data is currently being refreshed.
   */
  isRefreshing?: boolean;

  /**
   * Optional callback to trigger a data refresh.
   */
  onRefresh?: () => void;

  /**
   * Time in milliseconds after which data is considered stale.
   * Default: 5 minutes (300000ms)
   */
  staleAfterMs?: number;

  /**
   * Whether to show the refresh button.
   * Default: true if onRefresh is provided
   */
  showRefreshButton?: boolean;

  /**
   * Whether the user is currently offline.
   * When true, shows a cached data warning.
   */
  isOffline?: boolean;

  /**
   * Additional CSS classes.
   */
  className?: string;

  /**
   * Size variant for the indicator.
   * Default: "default"
   */
  size?: "sm" | "default";
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_STALE_AFTER_MS = 5 * 60 * 1000; // 5 minutes
const UPDATE_INTERVAL_MS = 30 * 1000; // Update relative time every 30 seconds

// ============================================================================
// Component
// ============================================================================

/**
 * StaleDataIndicator
 *
 * Displays the freshness status of data with relative timestamps.
 * Shows visual warnings when data is stale or when viewing cached data offline.
 *
 * @param props - Component props
 * @returns The StaleDataIndicator component
 */
export function StaleDataIndicator({
  dataUpdatedAt,
  isRefreshing = false,
  onRefresh,
  staleAfterMs = DEFAULT_STALE_AFTER_MS,
  showRefreshButton,
  isOffline = false,
  className,
  size = "default",
}: StaleDataIndicatorProps) {
  // Track the formatted time string and update it periodically
  const [formattedTime, setFormattedTime] = useState(() =>
    formatLastUpdated(dataUpdatedAt)
  );
  const [isStale, setIsStale] = useState(() =>
    isDataStale(dataUpdatedAt, staleAfterMs)
  );

  // Update the relative time display periodically
  useEffect(() => {
    const updateDisplay = () => {
      setFormattedTime(formatLastUpdated(dataUpdatedAt));
      setIsStale(isDataStale(dataUpdatedAt, staleAfterMs));
    };

    // Update immediately when dataUpdatedAt changes
    updateDisplay();

    // Set up interval to update the relative time
    const interval = setInterval(updateDisplay, UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dataUpdatedAt, staleAfterMs]);

  // Determine if refresh button should be shown
  const shouldShowRefresh = showRefreshButton ?? !!onRefresh;

  // Determine status variant
  const variant = isOffline ? "warning" : isStale ? "stale" : "normal";

  const sizeClasses = {
    sm: {
      container: "gap-1 text-xs",
      icon: "h-3 w-3",
      button: "h-5 w-5 p-0.5",
    },
    default: {
      container: "gap-1.5 text-xs",
      icon: "h-3.5 w-3.5",
      button: "h-6 w-6 p-1",
    },
  };

  const variantClasses = {
    normal: "text-muted-foreground",
    stale: "text-amber-600 dark:text-amber-500",
    warning: "text-amber-700 dark:text-amber-400",
  };

  return (
    <div
      className={cn(
        "inline-flex items-center",
        sizeClasses[size].container,
        variantClasses[variant],
        className
      )}
      role="status"
      aria-label={`Data freshness: ${formattedTime}`}
    >
      {/* Icon */}
      {variant === "warning" ? (
        <AlertCircle
          className={cn(sizeClasses[size].icon, "shrink-0")}
          aria-hidden="true"
        />
      ) : (
        <Clock
          className={cn(sizeClasses[size].icon, "shrink-0")}
          aria-hidden="true"
        />
      )}

      {/* Timestamp text */}
      <span className="whitespace-nowrap">
        {isOffline ? `Cached: ${formattedTime.replace("Last updated ", "").replace("Updated ", "")}` : formattedTime}
      </span>

      {/* Refresh button */}
      {shouldShowRefresh && onRefresh && (
        <button
          onClick={onRefresh}
          disabled={isRefreshing || isOffline}
          className={cn(
            "inline-flex items-center justify-center rounded-md",
            "transition-colors duration-150",
            "hover:bg-accent hover:text-accent-foreground",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "disabled:pointer-events-none disabled:opacity-50",
            sizeClasses[size].button
          )}
          aria-label={isRefreshing ? "Refreshing data..." : "Refresh data"}
        >
          <RefreshCw
            className={cn(
              sizeClasses[size].icon,
              isRefreshing && "animate-spin"
            )}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}

/**
 * Compact inline version for use in headers or tight spaces.
 */
export function StaleDataBadge({
  dataUpdatedAt,
  isOffline = false,
  className,
}: {
  dataUpdatedAt: number | Date | null | undefined;
  isOffline?: boolean;
  className?: string;
}) {
  const [formattedTime, setFormattedTime] = useState(() =>
    formatLastUpdated(dataUpdatedAt)
  );

  useEffect(() => {
    const updateDisplay = () => {
      setFormattedTime(formatLastUpdated(dataUpdatedAt));
    };

    updateDisplay();
    const interval = setInterval(updateDisplay, UPDATE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  // Extract just the time part
  const timeOnly = formattedTime
    .replace("Last updated ", "")
    .replace("Updated ", "");

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        "text-xs text-muted-foreground",
        isOffline && "text-amber-600 dark:text-amber-500",
        className
      )}
    >
      <Clock className="h-3 w-3" aria-hidden="true" />
      {timeOnly}
    </span>
  );
}

export default StaleDataIndicator;
